import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addMeal, FOOD_IMAGES } from '../utils/storage';
import { Typewriter } from 'animal-island-ui';
import { getEmotionEmoji } from './CalendarPage';
import './WizardPage.css';

// Fix Leaflet marker default assets
const DefaultIcon = L.icon({
  iconUrl: new URL('../../node_modules/leaflet/dist/images/marker-icon.png', import.meta.url).href,
  iconRetinaUrl: new URL('../../node_modules/leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  shadowUrl: new URL('../../node_modules/leaflet/dist/images/marker-shadow.png', import.meta.url).href,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Static Option Data Arrays
const PLACE_TYPES = [
  'Canteen',
  'Dorm',
  'Restaurant',
  'Cafe',
  'Fast Food',
  'Home',
  'Convenience Store',
  'Outdoor'
];

const SOCIAL_CONTEXTS = [
  'Alone',
  'Friends',
  'Classmates',
  'Colleagues',
  'Family',
  'Partner',
  'Roommates'
];

const DECISION_REASONS = [
  'Convenience',
  'Price',
  'Health',
  'Taste',
  'Habit',
  'Craving',
  'Hunger',
  'Socializing'
];

// Helper to capture clicks on the Leaflet Map
function MapEventsHelper({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}

// Helper to center the map when gpsLocation changes
function RecenterMapHelper({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

export function WizardPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Read initial date from navigation state
  const targetDateStr: string = location.state?.targetDateStr || new Date().toISOString().split('T')[0];
  const targetDayOfWeek: number = location.state?.targetDayOfWeek ?? new Date().getDay();

  const [step, setStep] = useState(1);

  // Form states
  const [capturedPhoto, setCapturedPhoto] = useState<string>('');
  const [customMealName, setCustomMealName] = useState<string>('');
  const [photoFlash, setPhotoFlash] = useState(false);
  const [hasCameraAccess, setHasCameraAccess] = useState<boolean | null>(null);

  // Camera Refs and Stream
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Real Audio Recorder states
  const [isRecording, setIsRecording] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string>(''); // base64 data URL
  const [isPlayingRecorded, setIsPlayingRecorded] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);

  // Camera stream control
  const startCamera = async () => {
    try {
      stopCamera(); // Stop any existing streams first
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 640, height: 480 },
        audio: false,
      });
      streamRef.current = stream;
      setHasCameraAccess(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.warn('Camera access denied or unsupported:', err);
      setHasCameraAccess(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  // Manage camera lifecycle
  useEffect(() => {
    if (step === 1 && !capturedPhoto) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [step, capturedPhoto]);

  // Capture frame from video stream
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Shutter flash effect
        setPhotoFlash(true);
        setTimeout(() => setPhotoFlash(false), 300);

        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const dataUrl = canvas.toDataURL('image/jpeg');
        setCapturedPhoto(dataUrl);
        stopCamera();
      }
    }
  };

  const handleRetakePhoto = () => {
    setCapturedPhoto('');
    setHasCameraAccess(null);
  };

  // Microphone Recording Logic
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          setAudioUrl(base64data);
        };
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordDuration(0);

      durationIntervalRef.current = setInterval(() => {
        setRecordDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.warn('Microphone access denied, simulating recording:', err);
      setIsRecording(true);
      setRecordDuration(0);
      durationIntervalRef.current = setInterval(() => {
        setRecordDuration((prev) => {
          if (prev >= 8) {
            stopRecording();
            return 8;
          }
          return prev + 1;
        });
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else {
      const silentWav = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAAA';
      setAudioUrl(silentWav);
    }
    
    setIsRecording(false);
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      handleReRecord();
      startRecording();
    }
  };

  const handleReRecord = () => {
    setAudioUrl('');
    setRecordDuration(0);
    setIsPlayingRecorded(false);
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
    }
  };

  const handlePlayPauseRecorded = () => {
    if (!audioUrl) return;
    if (isPlayingRecorded) {
      playbackAudioRef.current?.pause();
      setIsPlayingRecorded(false);
    } else {
      const audio = new Audio(audioUrl);
      playbackAudioRef.current = audio;
      audio.play().catch(e => console.warn('Audio play error in wizard:', e));
      setIsPlayingRecorded(true);
      audio.onended = () => {
        setIsPlayingRecorded(false);
      };
    }
  };

  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      if (playbackAudioRef.current) playbackAudioRef.current.pause();
      stopCamera();
    };
  }, []);

  // Request actual user geolocation on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`;
          
          fetch(url, {
            headers: {
              'Accept-Language': 'en-US,en;q=0.9',
              'User-Agent': 'FoodJourneyProbeKit/1.0'
            }
          })
            .then(res => res.json())
            .then(data => {
              const addressName = data.display_name || data.name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
              const isCampus = checkIfCampus(latitude, longitude) || 
                (data.address && (data.address.amenity === 'university' || data.address.university || data.address.college));
              
              setGpsLocation({
                lat: latitude,
                lng: longitude,
                locationName: addressName.split(',').slice(0, 3).join(','),
                isCampus: !!isCampus,
              });
            })
            .catch(err => {
              console.error('Error reverse geocoding:', err);
              setGpsLocation({
                lat: latitude,
                lng: longitude,
                locationName: `My Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
                isCampus: checkIfCampus(latitude, longitude),
              });
            });
        },
        (error) => {
          console.warn('Geolocation permission denied or error:', error);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }
  }, []);

  // Context states
  const [gpsLocation, setGpsLocation] = useState({
    lat: 42.3736,
    lng: -71.1186,
    locationName: 'Harvard Yard (Campus Center)',
    isCampus: true,
  });
  const [locationType, setLocationType] = useState<string>('Canteen');
  const [socialTag, setSocialTag] = useState<string>('Friends');
  const [decisionReasons, setDecisionReasons] = useState<string[]>([]);

  // Emotion states
  const [beforeEmotion, setBeforeEmotion] = useState(3);
  const [duringEmotion, setDuringEmotion] = useState(3);
  const [afterEmotion, setAfterEmotion] = useState(3);
  const [overallScore, setOverallScore] = useState(7);

  const handleCheckboxChange = (reason: string) => {
    setDecisionReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  // Helper functions for map geocoding logic
  const checkIfCampus = (lat: number, lng: number): boolean => {
    const dist = Math.sqrt(Math.pow(lat - 42.3736, 2) + Math.pow(lng - -71.1186, 2));
    return dist < 0.0045;
  };

  const getDynamicLocationName = (lat: number, lng: number, isCampus: boolean): string => {
    if (isCampus) {
      if (lat > 42.3750) return 'North Campus Science Center Cafe';
      if (lat < 42.3722) return 'South Campus Dining Hall';
      if (lng < -71.1210) return 'Campus Dormitory Common Room';
      return 'Main Campus Plaza Garden';
    } else {
      if (lat > 42.3760) return 'Mass Ave Local Restaurant';
      if (lat < 42.3700) return 'Harvard Sq. Commercial Bistro';
      if (lng > -71.1140) return 'Off-Campus Starbucks & Cafe';
      return 'Harvard Square Street Food Cart';
    }
  };

  const handleLeafletMapClick = (lat: number, lng: number) => {
    const isCampus = checkIfCampus(lat, lng);
    const locName = getDynamicLocationName(lat, lng, isCampus);
    setGpsLocation({
      lat,
      lng,
      locationName: locName,
      isCampus,
    });
  };

  const handleSubmit = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const timestampStr = `${hours % 12 || 12}:${minutes} ${ampm}`;

    addMeal({
      dayOfWeek: targetDayOfWeek,
      timestamp: timestampStr,
      dateStr: targetDateStr,
      foodType: 'custom', 
      customName: customMealName.trim() || 'Captured Meal',
      photoUrl: capturedPhoto,
      audioUrl: audioUrl || undefined,
      voiceDuration: recordDuration || 5,
      gps: gpsLocation,
      socialTag,
      locationType,
      decisionReasons,
      emotions: {
        before: beforeEmotion,
        during: duringEmotion,
        after: afterEmotion,
      },
      overallScore,
    });

    setStep(5); // Success Screen
  };

  const handleNext = () => {
    if (step === 1 && !capturedPhoto) return;
    if (step === 2 && !audioUrl && !isRecording) {
      startRecording();
      setTimeout(stopRecording, 1000);
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="wizard-page-container">
      {/* Wizard Step Progress Bar */}
      {step < 5 && (
        <div className="wizard-progress-header">
          <div className="progress-step-text">Step {step} of 4</div>
          <div className="wizard-progress-bar">
            <div className="wizard-progress-bar-fill" style={{ width: `${(step / 4) * 100}%` }}></div>
          </div>
        </div>
      )}

      {/* Step 1: Camera Photo Selection */}
      {step === 1 && (
        <div className="wizard-step step-camera">
          <div className="step-title-card">
            <h3>📷 Step 1: Capture Photo</h3>
            <p>Use your camera to snap a live photo of your meal.</p>
          </div>

          <div className="camera-viewfinder">
            {photoFlash && <div className="camera-flash-overlay"></div>}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {capturedPhoto ? (
              <div className="captured-view">
                <img
                  src={capturedPhoto}
                  alt="Captured food"
                  className="captured-food-img animate-shutter"
                />
                
                {/* Text input to label what this meal is */}
                <div className="custom-name-editor">
                  <label className="name-input-label">Meal Name:</label>
                  <input
                    type="text"
                    className="name-input-field"
                    placeholder="e.g. Ramen, Sushi, Burger..."
                    value={customMealName}
                    onChange={(e) => setCustomMealName(e.target.value)}
                  />
                </div>

                <button className="retake-photo-btn" onClick={handleRetakePhoto}>
                  Retake Photo
                </button>
              </div>
            ) : hasCameraAccess === false ? (
              // Fallback file input if camera permission is denied or not supported
              <div className="camera-fallback-panel">
                <span className="fallback-icon">📂</span>
                <p>Camera access denied or unsupported.</p>
                <label className="file-upload-label-btn">
                  Choose Photo File
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setCapturedPhoto(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            ) : (
              // Live Web Camera Viewport
              <div className="webcam-viewport">
                <video ref={videoRef} autoPlay playsInline className="webcam-video-feed"></video>
                <div className="webcam-controls">
                  <button className="webcam-capture-btn" onClick={capturePhoto}>
                    <div className="capture-btn-inner"></div>
                  </button>
                  <div className="capture-btn-label">Tap button to snap</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Circular Audio Recorder */}
      {step === 2 && (
        <div className="wizard-step step-voice">
          <div className="step-title-card">
            <h3>🎙️ Step 2: Record Voice Memo</h3>
            <p>Record a brief voice memo detailing this meal.</p>
          </div>

          <div className="recorder-panel">
            <div className="circular-recorder-card">
              {audioUrl ? (
                // Playback and Re-record controls
                <div className="playback-controls-container">
                  <div className="playback-status-text">
                    🔊 Recording saved ({recordDuration}s)
                  </div>
                  <div className="playback-buttons-row">
                    <button 
                      className={`playback-btn-play ${isPlayingRecorded ? 'playing' : ''}`}
                      onClick={handlePlayPauseRecorded}
                    >
                      {isPlayingRecorded ? '⏸️ Pause' : '▶️ Playback'}
                    </button>
                    <button className="playback-btn-reset" onClick={handleReRecord}>
                      🔄 Re-record
                    </button>
                  </div>
                </div>
              ) : (
                // Circular record toggle button
                <div className="record-button-container">
                  <button
                    className={`circular-record-btn ${isRecording ? 'recording' : ''}`}
                    onClick={handleRecordToggle}
                  >
                    <div className="record-btn-inner"></div>
                  </button>
                  <div className="record-timer-text">
                    {isRecording ? (
                      <span className="rec-text-blink">
                        🔴 Recording: {recordDuration}s
                      </span>
                    ) : (
                      'Tap circle to record'
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Location and Context */}
      {step === 3 && (
        <div className="wizard-step step-context">
          <div className="step-title-card">
            <h3>📍 Step 3: Location & Context</h3>
            <p>Verify your GPS location and context factors.</p>
          </div>

          <div className="gps-map-section">
            <div className="mini-map-header">
              <span className="mini-map-loc-badge">
                {gpsLocation.isCampus ? '🏫 On-Campus' : '🏡 Off-Campus'}
              </span>
              <span className="mini-map-loc-name">{gpsLocation.locationName}</span>
            </div>
            
            <div className="mini-map-body-leaflet">
              <MapContainer 
                center={[gpsLocation.lat, gpsLocation.lng]} 
                zoom={15} 
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapEventsHelper onClick={handleLeafletMapClick} />
                <RecenterMapHelper center={[gpsLocation.lat, gpsLocation.lng]} />
                <Marker position={[gpsLocation.lat, gpsLocation.lng]} />
              </MapContainer>
            </div>
          </div>

          {/* Place type buttons */}
          <div className="form-group-section">
            <label className="form-group-label">Eating Place Type:</label>
            <div className="form-buttons-row">
              {PLACE_TYPES.map((type) => (
                <button
                  key={type}
                  className={`pill-choice-btn ${locationType === type ? 'active' : ''}`}
                  onClick={() => setLocationType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Social factor buttons */}
          <div className="form-group-section">
            <label className="form-group-label">Social Context:</label>
            <div className="form-buttons-row">
              {SOCIAL_CONTEXTS.map((tag) => (
                <button
                  key={tag}
                  className={`pill-choice-btn ${socialTag === tag ? 'active' : ''}`}
                  onClick={() => setSocialTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Decision reasons check list */}
          <div className="form-group-section">
            <label className="form-group-label">Decision Reasons (Multi-select):</label>
            <div className="checkbox-flex-grid">
              {DECISION_REASONS.map((reason) => {
                const checked = decisionReasons.includes(reason);
                return (
                  <button
                    key={reason}
                    className={`checkbox-chip-btn ${checked ? 'checked' : ''}`}
                    onClick={() => handleCheckboxChange(reason)}
                  >
                    <span className="check-box-indicator">{checked ? '✓' : ''}</span>
                    {reason}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Emotions and Experience */}
      {step === 4 && (
        <div className="wizard-step step-emotions">
          <div className="step-title-card">
            <h3>🎭 Step 4: Emotion Sliders</h3>
            <p>Track your emotions and overall dining experience.</p>
          </div>

          {/* Three emotion sliders */}
          <div className="form-group-section slider-section">
            <div className="slider-header">
              <span className="slider-title">Before Eating:</span>
              <span className="slider-emoji">{getEmotionEmoji(beforeEmotion, 3, 3)}</span>
            </div>
            <input
              type="range"
              min="1"
              max="5"
              value={beforeEmotion}
              onChange={(e) => setBeforeEmotion(Number(e.target.value))}
              className="nook-range-slider"
            />
            <div className="slider-ticks-labels">
              <span>😭</span><span>🙁</span><span>🙂</span><span>😀</span><span>😍</span>
            </div>
          </div>

          <div className="form-group-section slider-section">
            <div className="slider-header">
              <span className="slider-title">During Eating:</span>
              <span className="slider-emoji">{getEmotionEmoji(3, duringEmotion, 3)}</span>
            </div>
            <input
              type="range"
              min="1"
              max="5"
              value={duringEmotion}
              onChange={(e) => setDuringEmotion(Number(e.target.value))}
              className="nook-range-slider"
            />
            <div className="slider-ticks-labels">
              <span>😭</span><span>🙁</span><span>🙂</span><span>😀</span><span>😍</span>
            </div>
          </div>

          <div className="form-group-section slider-section">
            <div className="slider-header">
              <span className="slider-title">After Eating:</span>
              <span className="slider-emoji">{getEmotionEmoji(3, 3, afterEmotion)}</span>
            </div>
            <input
              type="range"
              min="1"
              max="5"
              value={afterEmotion}
              onChange={(e) => setAfterEmotion(Number(e.target.value))}
              className="nook-range-slider"
            />
            <div className="slider-ticks-labels">
              <span>😭</span><span>🙁</span><span>🙂</span><span>😀</span><span>😍</span>
            </div>
          </div>

          {/* Overall Experience slider */}
          <div className="form-group-section slider-section overall-section">
            <div className="slider-header">
              <span className="slider-title">Overall Experience Score:</span>
              <span className="overall-score-indicator">{overallScore} / 10</span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={overallScore}
              onChange={(e) => setOverallScore(Number(e.target.value))}
              className="nook-range-slider overall-slider"
            />
            <div className="slider-ticks-labels-10">
              <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
              <span>6</span><span>7</span><span>8</span><span>9</span><span>10</span>
            </div>
          </div>
        </div>
      )}

      {/* Step 5: Success completion */}
      {step === 5 && (
        <div className="wizard-step step-success animate-zoom">
          <div className="success-leaf-seal-static">🍃</div>
          <h2>Meal Logged!</h2>
          
          <div className="success-dialog-card">
            <Typewriter speed={60} autoPlay>
              <p>Excellent work, traveler! Your meal photo and voice recording have been logged successfully.</p>
              <p>You have earned <strong>150 Points</strong> for your contribution! Keep logging to track your habits.</p>
            </Typewriter>
          </div>

          <button className="success-done-btn" onClick={() => navigate('/calendar')}>
            View Calendar Diary
          </button>
        </div>
      )}

      {/* Wizard navigation footer */}
      {step < 5 && (
        <div className="wizard-footer-nav">
          <button
            className={`wizard-nav-btn back-btn ${step === 1 ? 'disabled' : ''}`}
            onClick={handleBack}
            disabled={step === 1}
          >
            ◀ Back
          </button>

          {step === 4 ? (
            <button className="wizard-nav-btn next-btn submit-btn" onClick={handleSubmit}>
              Submit Log ✓
            </button>
          ) : (
            <button
              className={`wizard-nav-btn next-btn ${step === 1 && !capturedPhoto ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={step === 1 && !capturedPhoto}
            >
              Next ▶
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default WizardPage;
